// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'

export type Scope = 'col' | 'row'

/**
 * The `<qds-table-head-cell>` element defines a cell as the header of a group
 * of table cells. The exact nature of this group is defined by the `scope`
 * attribute.
 *
 * @see https://quartz.se.com/build/components/table
 */
@Component({
  tag: 'qds-table-head-cell',
  shadow: true,
  styleUrl: 'table-head-cell.css',
})
export class TableHeadCell implements ComponentInterface {
  /**
   * A short abbreviated description of the cell's content. Some user-agents,
   * such as speech readers, may present this description before the content
   * itself.
   *
   * @webnative
   */
  @Prop() public readonly abbr?: string

  /**
   * Non-negative integer value that indicates for how many columns the cell
   * extends. Its default value is `1`. Values higher than 1000 will be
   * considered as incorrect and will be set to the default value (1).
   *
   * @webnative
   */
  @Prop({ attribute: 'colspan' }) public readonly colSpan?: number

  /**
   * Non-negative integer value that indicates for how many rows the cell
   * extends. Its default value is `1`; if its value is set to `0`, it extends
   * until the end of the table section (`<qds-table-head>` or
   * `<qds-table-body>`, even if implicitly defined), that the cell belongs to.
   * Values higher than 65534 are clipped down to 65534.
   *
   * @webnative
   */
  @Prop({ attribute: 'rowspan' }) public readonly rowSpan?: number

  /**
   * Defines the cells that the header element relates to. It may have the
   * following values:
   *
   * - `row`: The header relates to all cells of the row it belongs to.
   * - `col`: The header relates to all cells of the column it belongs to.
   *
   * If the `scope` attribute is not specified, or its value is not `row` or
   * `col`, then browsers automatically select the set of cells to which the
   * header cell applies.
   *
   * @webnative
   */
  @Prop() public readonly scope?: Scope

  @Element() private readonly host!: HTMLElement

  #inheritedAttributes: Attributes = {}

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <th
        class="qds-table-head-cell"
        abbr={this.abbr}
        colSpan={this.colSpan}
        rowSpan={this.rowSpan}
        scope={this.scope}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        <slot />
      </th>
    )
  }
}
